import React, { useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  AlertTriangle, ShieldAlert, UserX, Info, Globe, Plus, Loader2,
  ChevronDown, ChevronUp,
} from 'lucide-react';
import { toast } from 'sonner';
import { addCaseInvestigatorNote } from '@/lib/api';

// ── Note-kind visual config ────────────────────────────────────────
// Map investigator_notes.kind → badge colour + label + icon. Any kind
// not in this table falls back to a neutral slate badge with the raw
// kind string uppercased. Colour choices mirror the enrichment-flag
// colours on the case header so a reader can eyeball a note and a
// flag and see they're the same thing.
const NOTE_KIND_META = {
  case_presumed_solved: {
    label: 'HOMICIDE CONVICTION',
    cls: 'bg-red-700 text-white hover:bg-red-700',
    icon: ShieldAlert,
  },
  active_homicide_investigation: {
    label: 'ACTIVE HOMICIDE',
    cls: 'bg-orange-500 text-white hover:bg-orange-500',
    icon: ShieldAlert,
  },
  witnessed_incident: {
    label: 'WITNESSED INCIDENT',
    cls: 'bg-amber-500 text-white hover:bg-amber-500',
    icon: AlertTriangle,
  },
  person_of_interest: {
    label: 'PERSON OF INTEREST',
    cls: 'bg-red-600 text-white hover:bg-red-600',
    icon: UserX,
  },
  web_enrichment: {
    label: 'WEB ENRICHMENT',
    cls: 'bg-blue-600 text-white hover:bg-blue-600',
    icon: Globe,
  },
};

const KIND_OPTIONS = Object.entries(NOTE_KIND_META).map(([k, v]) => ({
  value: k, label: v.label,
})).concat([{ value: 'other', label: 'OTHER' }]);

function NoteKindBadge({ kind }) {
  const meta = NOTE_KIND_META[kind];
  if (!meta) {
    return (
      <Badge className="bg-slate-500 text-white hover:bg-slate-500 text-[10px] uppercase tracking-wider">
        <Info className="w-3 h-3 mr-1" />
        {(kind || 'other').replace(/_/g, ' ')}
      </Badge>
    );
  }
  const Icon = meta.icon;
  return (
    <Badge className={`${meta.cls} text-[10px] uppercase tracking-wider`}>
      <Icon className="w-3 h-3 mr-1" />
      {meta.label}
    </Badge>
  );
}

// ── Header enrichment-flag strip ───────────────────────────────────
// Compact colour-coded pills shown alongside the status badge in the
// case header. Mirrors `investigator_notes` kinds but driven by
// `caseData.flags` — an enrichment flag can exist WITHOUT a matching
// note (e.g., older data).
const FLAG_META = {
  ACTIVE_HOMICIDE_INVESTIGATION: {
    label: 'ACTIVE HOMICIDE',
    cls: 'bg-orange-500 text-white hover:bg-orange-500',
    icon: ShieldAlert,
  },
  PERSON_OF_INTEREST_ON_FILE: {
    label: 'PERSON OF INTEREST',
    cls: 'bg-red-600 text-white hover:bg-red-600',
    icon: UserX,
  },
  HOMICIDE_CONVICTION_NO_REMAINS: {
    label: 'CONVICTION · NO REMAINS',
    cls: 'bg-red-800 text-white hover:bg-red-800',
    icon: ShieldAlert,
  },
  WITNESSED_FALL_PRESUMED_DROWNED: {
    label: 'WITNESSED INCIDENT',
    cls: 'bg-amber-500 text-white hover:bg-amber-500',
    icon: AlertTriangle,
  },
  WEB_ENRICHED_2026: {
    label: 'WEB ENRICHED 2026',
    cls: 'bg-blue-600 text-white hover:bg-blue-600',
    icon: Globe,
  },
};

export function EnrichmentFlagBadges({ flags }) {
  if (!Array.isArray(flags) || flags.length === 0) return null;
  const present = flags.filter((f) => FLAG_META[f]);
  if (present.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-1.5" data-testid="enrichment-flag-badges">
      {present.map((f) => {
        const meta = FLAG_META[f];
        const Icon = meta.icon;
        return (
          <Badge
            key={f}
            className={`${meta.cls} text-[10px] uppercase tracking-wider flex items-center gap-1`}
            title={f}
            data-testid={`enrichment-flag-${f.toLowerCase()}`}>
            <Icon className="w-3 h-3" />
            {meta.label}
          </Badge>
        );
      })}
    </div>
  );
}

// ── Main panel ─────────────────────────────────────────────────────
function _fmtDate(iso) {
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: 'numeric', month: 'short', day: 'numeric',
    });
  } catch {
    return iso;
  }
}

export default function CaseInvestigatorNotes({
  caseId, notes: initialNotes, isAdmin = false, onNoteAdded,
}) {
  // Normalize incoming notes — the DB carries two historical shapes:
  //   (legacy)  {type, text, timestamp, author, source, priority}
  //   (2026-04) {kind, body, created_at, author, ...}
  // Both map onto the same display fields here.
  const normalize = (n) => ({
    id: n.id
      || `${n.type || n.kind || 'note'}-${n.timestamp || n.created_at || ''}`,
    kind: n.kind || n.type || 'other',
    body: n.body || n.text || '',
    author: n.author,
    created_at: n.created_at || n.timestamp,
    agency: n.agency,
    agency_case_number: n.agency_case_number,
    person_of_interest: n.person_of_interest,
    reviewed_by: n.reviewed_by,
    review_date: n.review_date,
    source: n.source,
    priority: n.priority,
  });

  const [notes, setNotes] = useState(
    Array.isArray(initialNotes) ? initialNotes.map(normalize) : []);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ kind: 'web_enrichment', body: '' });
  const [saving, setSaving] = useState(false);
  const [expanded, setExpanded] = useState(false);

  // Nothing to show + not an admin → panel stays mounted but empty.
  // The panel header stays so a viewer sees "this case has no notes"
  // rather than assuming the feature doesn't exist.
  const sorted = [...notes].sort((a, b) => {
    const da = a.created_at || ''; const db = b.created_at || '';
    return db.localeCompare(da);  // newest first
  });

  // When there are 5+ notes, collapse to the 2 most recent by default
  // so the panel doesn't crowd out the photo + hero content. Tapping
  // "Show all N notes" reveals the rest inline.
  const COLLAPSE_THRESHOLD = 5;
  const COLLAPSED_COUNT = 2;
  const shouldCollapse = sorted.length >= COLLAPSE_THRESHOLD && !expanded;
  const visibleNotes = shouldCollapse ? sorted.slice(0, COLLAPSED_COUNT) : sorted;

  const handleAdd = async () => {
    if (!form.body.trim()) {
      toast.error('Note body is required');
      return;
    }
    setSaving(true);
    try {
      const res = await addCaseInvestigatorNote(caseId, {
        kind: form.kind, body: form.body.trim(),
      });
      const newNote = res?.note;
      if (newNote) setNotes((prev) => [...prev, normalize(newNote)]);
      setForm({ kind: 'web_enrichment', body: '' });
      setOpen(false);
      toast.success('Investigator note added');
      if (onNoteAdded) onNoteAdded(newNote);
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Failed to add note';
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      data-testid="investigator-notes-panel"
      className="mt-4 p-4 border border-slate-200 dark:border-slate-700 rounded-lg bg-slate-50 dark:bg-slate-900/30">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h3 className="font-semibold text-base flex items-center gap-2">
            <ShieldAlert className="w-4 h-4 text-slate-600 dark:text-slate-400" />
            Investigator Notes
            {sorted.length > 0 && (
              <span className="text-xs font-normal text-slate-500">
                ({sorted.length})
              </span>
            )}
          </h3>
          <p className="text-xs text-slate-500 mt-0.5">
            Curator-verified case context. Visible to all logged-in users.
          </p>
        </div>
        {isAdmin && !open && (
          <Button
            size="sm"
            variant="outline"
            onClick={() => setOpen(true)}
            data-testid="add-investigator-note-btn">
            <Plus className="w-4 h-4 mr-1" /> Add Note
          </Button>
        )}
      </div>

      {isAdmin && open && (
        <div className="mb-4 p-3 bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-600 rounded"
             data-testid="add-investigator-note-form">
          <div className="flex gap-2 mb-2">
            <Select
              value={form.kind}
              onValueChange={(v) => setForm((f) => ({ ...f, kind: v }))}>
              <SelectTrigger className="w-56"
                             data-testid="note-kind-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {KIND_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value}>
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Textarea
            placeholder="Investigator-verified context (1-3 sentences; cite agencies and case numbers where possible)"
            value={form.body}
            onChange={(e) => setForm((f) => ({ ...f, body: e.target.value }))}
            rows={4}
            className="mb-2"
            data-testid="note-body-input"
          />
          <div className="flex justify-end gap-2">
            <Button size="sm" variant="ghost" onClick={() => setOpen(false)}
                    disabled={saving}>
              Cancel
            </Button>
            <Button size="sm" onClick={handleAdd} disabled={saving}
                    data-testid="note-save-btn">
              {saving ? (
                <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Saving</>
              ) : 'Save Note'}
            </Button>
          </div>
        </div>
      )}

      {sorted.length === 0 ? (
        <div className="text-xs text-slate-400 italic py-3 text-center">
          No investigator notes on this case yet.
        </div>
      ) : (
        <>
          <div
            data-testid="investigator-notes-list"
            className={
              `space-y-2.5 ${shouldCollapse ? '' : 'max-h-[320px] overflow-y-auto pr-1'} `
              + 'scrollbar-thin scrollbar-thumb-slate-300 scrollbar-track-transparent '
              + 'dark:scrollbar-thumb-slate-600'
            }>
            {visibleNotes.map((n) => (
              <div
                key={n.id}
                className="p-3 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-600 rounded"
                data-testid={`investigator-note-${n.id}`}>
                <div className="flex items-center justify-between mb-1.5 flex-wrap gap-2">
                  <NoteKindBadge kind={n.kind} />
                  <div className="text-[11px] text-slate-500">
                    {_fmtDate(n.created_at)}
                    {n.author && <> · {n.author}</>}
                  </div>
                </div>
                <div className="text-sm text-slate-700 dark:text-slate-200 whitespace-pre-wrap leading-relaxed">
                  {n.body}
                </div>
                {(n.agency || n.agency_case_number) && (
                  <div className="text-[11px] text-slate-500 mt-1.5 font-mono">
                    {n.agency}
                    {n.agency_case_number && ` · case ${n.agency_case_number}`}
                  </div>
                )}
                {n.person_of_interest && (
                  <div className="text-[11px] text-rose-600 dark:text-rose-400 mt-1.5 font-semibold">
                    POI: {n.person_of_interest}
                  </div>
                )}
                {(n.reviewed_by || n.review_date) && (
                  <div className="text-[11px] text-slate-500 mt-1.5 italic">
                    Reviewed by {n.reviewed_by}
                    {n.review_date && ` · ${n.review_date}`}
                  </div>
                )}
              </div>
            ))}
          </div>
          {sorted.length >= COLLAPSE_THRESHOLD && (
            <div className="mt-3 flex justify-center">
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setExpanded((v) => !v)}
                data-testid="investigator-notes-toggle"
                className="text-xs">
                {expanded ? (
                  <><ChevronUp className="w-3.5 h-3.5 mr-1" /> Collapse</>
                ) : (
                  <><ChevronDown className="w-3.5 h-3.5 mr-1" /> Show all {sorted.length} notes</>
                )}
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
